import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ShieldCheck, Shield, Camera, Upload, CheckCircle2, 
  Clock, AlertTriangle, Loader2, Copy, Check, FileImage
} from 'lucide-react';
import api from '@/utils/api';

/**
 * Listing Verification Badge - Shows on item cards and detail pages
 */
export const VerificationBadge = ({ status, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };
  
  const configs = {
    verified: {
      icon: ShieldCheck,
      label: 'Verified Listing',
      color: 'text-emerald-500',
      bg: 'bg-emerald-50 border-emerald-200'
    },
    pending_review: {
      icon: Clock,
      label: 'Verification Pending',
      color: 'text-amber-500',
      bg: 'bg-amber-50 border-amber-200'
    },
    rejected: {
      icon: AlertTriangle,
      label: 'Verification Failed',
      color: 'text-red-500',
      bg: 'bg-red-50 border-red-200'
    }
  };
  
  const config = configs[status];
  if (!config) return null;
  
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`${config.bg} ${config.color}`}>
      <Icon className={`${sizeClasses[size]} mr-1`} />
      {config.label}
    </Badge>
  );
};

/**
 * Listing Verification Card - For sellers to verify their listings
 */
export const ListingVerificationCard = ({ listingId, currentStatus, isOwner }) => {
  const [showModal, setShowModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('intro'); // intro, code, upload, success
  const [selectedImage, setSelectedImage] = useState(null);
  const [notes, setNotes] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  
  // Fetch or generate verification code
  const getVerificationCode = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/listings/${listingId}/verification-code`);
      setVerificationCode(res.data.verification_code);
      setStep('code');
    } catch (error) {
      toast.error('Failed to get verification code');
    } finally {
      setLoading(false);
    }
  };
  
  const copyCode = () => {
    navigator.clipboard.writeText(verificationCode);
    setCodeCopied(true);
    toast.success('Code copied to clipboard');
    setTimeout(() => setCodeCopied(false), 2000);
  };
  
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setSelectedImage(event.target.result);
      setStep('upload');
    };
    reader.readAsDataURL(file);
  };
  
  const submitVerification = async () => {
    if (!selectedImage) {
      toast.error('Please select a verification image');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/listings/${listingId}/verify`, {
        verification_image: selectedImage,
        verification_type: 'photo',
        verification_notes: notes
      });
      
      toast.success('Verification submitted! We will review it shortly.');
      setStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setLoading(false);
    }
  };
  
  // Don't show if already verified or not owner
  if (currentStatus === 'verified' || !isOwner) {
    return currentStatus === 'verified' ? (
      <VerificationBadge status="verified" />
    ) : null;
  }
  
  return (
    <>
      <Card className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Shield className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-emerald-800">Verify Your Listing</h4>
            <p className="text-sm text-emerald-700 mt-1">
              Prove you own this item to boost buyer confidence and increase sales.
            </p>
            {currentStatus === 'pending_review' ? (
              <Badge variant="outline" className="mt-2 bg-amber-50 text-amber-700 border-amber-200">
                <Clock className="h-3 w-3 mr-1" />
                Verification Under Review
              </Badge>
            ) : currentStatus === 'rejected' ? (
              <div className="mt-2 space-y-2">
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Verification Rejected
                </Badge>
                <Button 
                  size="sm" 
                  onClick={() => setShowModal(true)}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  Try Again
                </Button>
              </div>
            ) : (
              <Button 
                size="sm" 
                onClick={() => setShowModal(true)}
                className="mt-2 bg-emerald-500 hover:bg-emerald-600"
              >
                <Camera className="h-4 w-4 mr-2" />
                Verify Now
              </Button>
            )}
          </div>
        </div>
      </Card>
      
      {/* Verification Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          {step === 'intro' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-emerald-500" />
                  Verify Your Listing
                </DialogTitle>
                <DialogDescription>
                  Prove you have the item to build trust with buyers.
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <h4 className="font-semibold text-emerald-800 mb-2">How it works:</h4>
                  <ol className="text-sm text-emerald-700 space-y-2 list-decimal list-inside">
                    <li>Get a unique verification code</li>
                    <li>Write the code on a piece of paper</li>
                    <li>Take a photo of the item WITH the code visible</li>
                    <li>Upload the photo for verification</li>
                  </ol>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Benefits:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-500" />
                      Get a "Verified Listing" badge
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-500" />
                      Increase buyer trust and sales
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-500" />
                      Priority placement in search results
                    </li>
                  </ul>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button 
                  onClick={getVerificationCode}
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Shield className="h-4 w-4 mr-2" />
                  )}
                  Get Verification Code
                </Button>
              </DialogFooter>
            </>
          )}
          
          {step === 'code' && (
            <>
              <DialogHeader>
                <DialogTitle>Your Verification Code</DialogTitle>
                <DialogDescription>
                  Write this code on paper and include it in your photo.
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-6 space-y-4">
                <div className="bg-slate-100 rounded-xl p-6 text-center">
                  <p className="text-4xl font-mono font-bold tracking-wider text-slate-800">
                    {verificationCode}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={copyCode}
                    className="mt-2"
                  >
                    {codeCopied ? (
                      <Check className="h-4 w-4 mr-2 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4 mr-2" />
                    )}
                    {codeCopied ? 'Copied!' : 'Copy Code'}
                  </Button>
                </div>
                
                <div className="bg-amber-50 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Important
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• Write this code clearly on paper</li>
                    <li>• The code must be visible in the photo</li>
                    <li>• The item must be clearly shown</li>
                    <li>• Photo must be taken today</li>
                  </ul>
                </div>
                
                <div className="text-center">
                  <input
                    type="file"
                    id="verification-image"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <label htmlFor="verification-image">
                    <Button asChild className="bg-emerald-500 hover:bg-emerald-600">
                      <span>
                        <Camera className="h-4 w-4 mr-2" />
                        Take or Upload Photo
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </>
          )}
          
          {step === 'upload' && (
            <>
              <DialogHeader>
                <DialogTitle>Review & Submit</DialogTitle>
                <DialogDescription>
                  Confirm your verification photo shows both the item and the code.
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                <div className="relative rounded-lg overflow-hidden border">
                  <img 
                    src={selectedImage} 
                    alt="Verification" 
                    className="w-full h-64 object-contain bg-slate-100"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2 bg-white"
                    onClick={() => {
                      setSelectedImage(null);
                      setStep('code');
                    }}
                  >
                    Change Photo
                  </Button>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-600">
                    Code to verify: <span className="font-mono font-bold">{verificationCode}</span>
                  </p>
                </div>
                
                <div>
                  <Label>Additional Notes (optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional information about the item..."
                    className="mt-1"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setStep('code')}>Back</Button>
                <Button 
                  onClick={submitVerification}
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Submit Verification
                </Button>
              </DialogFooter>
            </>
          )}
          
          {step === 'success' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                  Verification Submitted!
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-8 text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="h-10 w-10 text-emerald-500" />
                </div>
                <p className="text-slate-600 mb-4">
                  We'll review your verification and update your listing shortly.
                  This usually takes less than 24 hours.
                </p>
                <Button 
                  onClick={() => {
                    setShowModal(false);
                    window.location.reload();
                  }}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  Done
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Listing Verification Status Card - For item detail page (public view)
 */
export const ListingVerificationStatus = ({ listingId, status }) => {
  if (!status || status === 'not_verified') return null;
  
  return (
    <div className="flex items-center gap-2">
      <VerificationBadge status={status} size="md" />
    </div>
  );
};

export default ListingVerificationCard;
